/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NotificationService } from '@alfresco/adf-core';
import { ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PdfSigningService, SigningCertificateInfo } from './pdf-signing.service';

/**
 * The organisation's signing certificate, for administrators: what it is, the
 * public half to hand to readers, and a way to replace it with one a trusted
 * authority issued. The private key never comes to this page.
 */
@Component({
  selector: 'app-signing-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, ThaiDatePipe, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="aca-signset">
      <p class="aca-signset__intro">{{ 'DMS.SIGNING.INTRO' | translate }}</p>

      <p class="aca-signset__state" *ngIf="failed()"><mat-icon>cloud_off</mat-icon>{{ 'DMS.SIGNING.UNREACHABLE' | translate }}</p>

      <dl class="aca-signset__cert" *ngIf="cert() as c">
        <dt>{{ 'DMS.SIGNING.SUBJECT' | translate }}</dt>
        <dd>{{ c.subject }}</dd>
        <dt>{{ 'DMS.SIGNING.ISSUER' | translate }}</dt>
        <dd>{{ c.selfSigned ? ('DMS.SIGNING.SELF_SIGNED' | translate) : c.issuer }}</dd>
        <dt>{{ 'DMS.SIGNING.VALID' | translate }}</dt>
        <dd>{{ c.notBefore | acaDate: 'd MMM y' }} – {{ c.notAfter | acaDate: 'd MMM y' }}</dd>
        <dt>{{ 'DMS.SIGNING.THUMBPRINT' | translate }}</dt>
        <dd class="aca-signset__mono">{{ c.thumbprint }}</dd>
      </dl>

      <p class="aca-signset__hint" *ngIf="cert()?.selfSigned">{{ 'DMS.SIGNING.SELF_SIGNED_HINT' | translate }}</p>
      <a mat-stroked-button [href]="certificateUrl" download="organisation-signing.cer" *ngIf="cert()">
        <mat-icon>download</mat-icon>{{ 'DMS.SIGNING.DOWNLOAD' | translate }}
      </a>

      <h4>{{ 'DMS.SIGNING.REPLACE' | translate }}</h4>
      <p class="aca-signset__hint">{{ 'DMS.SIGNING.REPLACE_HINT' | translate }}</p>
      <div class="aca-signset__replace">
        <label class="aca-signset__file">
          <input type="file" accept=".pfx,.p12,application/x-pkcs12" (change)="pick($event)" />
          <mat-icon>upload_file</mat-icon>{{ file()?.name || ('DMS.SIGNING.PICK' | translate) }}
        </label>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'DMS.SIGNING.PASSWORD' | translate }}</mat-label>
          <input matInput type="password" [(ngModel)]="password" autocomplete="off" />
        </mat-form-field>
        <button mat-flat-button color="primary" type="button" (click)="replace()" [disabled]="!file() || busy()">
          {{ 'DMS.SIGNING.REPLACE_GO' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .aca-signset {
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-width: 720px;
      }
      .aca-signset__intro,
      .aca-signset__hint {
        margin: 0;
        font-size: 13px;
        line-height: 1.7;
        color: var(--aca-text-secondary);
      }
      .aca-signset__state {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        color: var(--aca-tone-amber-fg);
      }
      .aca-signset__cert {
        display: grid;
        grid-template-columns: max-content minmax(0, 1fr);
        gap: 6px 16px;
        margin: 0;
        font-size: 13px;
      }
      .aca-signset__cert dt {
        color: var(--aca-text-secondary);
      }
      .aca-signset__cert dd {
        margin: 0;
        overflow-wrap: anywhere;
      }
      .aca-signset__mono {
        font-family: monospace;
        font-size: 12px;
      }
      .aca-signset h4 {
        margin: 12px 0 0;
      }
      .aca-signset__replace {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
      }
      .aca-signset__file {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        border: 1px dashed var(--aca-hairline);
        border-radius: var(--aca-radius-sm);
        cursor: pointer;
      }
      .aca-signset__file input {
        display: none;
      }
    `
  ]
})
export class SigningSettingsComponent implements OnInit {
  private readonly signing = inject(PdfSigningService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  readonly cert = signal<SigningCertificateInfo | null>(null);
  readonly failed = signal(false);
  readonly busy = signal(false);
  readonly file = signal<File | null>(null);
  password = '';

  readonly certificateUrl = this.signing.certificateUrl;

  ngOnInit(): void {
    this.signing.certificate().subscribe({
      next: (cert) => this.cert.set(cert),
      error: () => this.failed.set(true)
    });
  }

  pick(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file.set(input.files?.[0] ?? null);
    input.value = '';
  }

  async replace(): Promise<void> {
    const file = this.file();
    if (!file) {
      return;
    }
    this.busy.set(true);
    const pfx = await file.arrayBuffer();
    this.signing.replaceCertificate(pfx, this.password).subscribe({
      next: (cert) => {
        this.busy.set(false);
        this.cert.set(cert);
        this.file.set(null);
        this.password = '';
        this.notification.showInfo(this.translate.instant('DMS.SIGNING.REPLACED'));
      },
      error: (err) => {
        this.busy.set(false);
        const reason = err?.error?.message;
        this.notification.showError(this.translate.instant('DMS.SIGNING.REPLACE_FAILED') + (reason ? ` — ${reason}` : ''));
      }
    });
  }
}
